import { effect, Injectable, signal } from '@angular/core';
import { BehaviorSubject, distinctUntilChanged, map, merge } from 'rxjs';
import { MediaConfigStore } from '../../state/media-config.repository';
import { BackgroundBlurService } from './background-blur.service';
import {
  DEFAULT_CAMERA_SETTINGS,
  DEFAULT_SCREEN_SETTINGS,
} from '../../shared/constants/video-settings';
import {
  buildCameraConstraints,
  buildSourceCameraConstraints,
  buildScreenConstraints,
} from '../../shared/helpers/video-constraints';
import { DeviceService } from './device.service';
import { MediaPermissionsService } from './media-permissions.service';
import { isMobile, isTauri } from '../../shared/helpers/platform';
import type { CaptureConfig, NativeCapture } from '@4players/odin-plugin-tauri/capture';
import type { VideoSettings } from '../../shared/constants/video-settings';

export type CamErrorKind = 'permission' | 'no-device' | 'in-use' | 'unknown';

/**
 * Structured camera start failure. `kind` drives the user-facing message;
 * `cause` is the underlying DOMException name for logging.
 */
export type CamError = { kind: CamErrorKind; cause: string };

/**
 * Maps a CamError to the user-facing message (an ngx-translate key — the
 * English text itself, per the en.json identity convention).
 */
export function camErrorMessage(error: CamError | null): string {
  switch (error?.kind) {
    case 'no-device':
      return 'No camera was found. Please connect a camera and try again.';
    case 'in-use':
      return 'The camera could not be started. It may be in use by another application.';
    case 'permission':
      return 'Camera access is blocked. Please allow camera access in your browser settings.';
    default:
      return 'The camera could not be started.';
  }
}

/**
 * Capture settings for the native pipeline. A "Source" resolution leaves
 * the size to the device. The app offers H.264 only (see `VALID_CODECS`).
 */
function nativeCaptureConfig(settings: VideoSettings): CaptureConfig {
  return {
    width: settings.resolution.width,
    height: settings.resolution.height,
    framerate: settings.maxFramerate,
    bitrate_bps: settings.maxBitrate * 1000,
    codec: 'H264',
  };
}

/** Whether this platform can share its screen. */
export const screenShareSupported = !isMobile;

function mapCamError(e: unknown): CamError {
  if (isTauri && e instanceof Error && /permission/i.test(e.message)) {
    return { kind: 'permission', cause: e.message };
  }
  const name = e instanceof Error ? e.name : 'unknown';
  switch (name) {
    case 'NotAllowedError':
    case 'SecurityError':
      return { kind: 'permission', cause: name };
    case 'NotFoundError':
    case 'OverconstrainedError':
      return { kind: 'no-device', cause: name };
    case 'NotReadableError':
    case 'AbortError':
      return { kind: 'in-use', cause: name };
    default:
      return { kind: 'unknown', cause: name };
  }
}

/**
 * Owns the local capture pipelines: the camera stream (including background
 * blur post-processing) and the screen-cast stream.
 *
 * Every camera mutation (start / stop / config-triggered restart) runs
 * through a serialization queue, so a stop can never interleave with an
 * in-flight start — the stream state after the queue drains always reflects
 * the last requested operation.
 */
@Injectable({
  providedIn: 'root',
})
export class LocalStreamService {
  private _camStream = new BehaviorSubject<MediaStream | null>(null);
  readonly camStream$ = this._camStream.asObservable();
  private _rawCamStream: MediaStream | null = null; // Unprocessed camera stream
  // Native (Tauri) captures behind `_camStream` / `_screenCastStream`.
  private _nativeCam: NativeCapture | null = null;
  private _nativeScreen: NativeCapture | null = null;
  private _screenCastStream = new BehaviorSubject<MediaStream | null>(null);
  readonly screenCastStream$ = this._screenCastStream.asObservable();

  private _camError = signal<CamError | null>(null);
  /** Last camera start failure; null after a successful start or a stop. */
  readonly camError = this._camError.asReadonly();

  private _queue: Promise<unknown> = Promise.resolve();

  constructor(
    private _mediaConfigStore: MediaConfigStore,
    private _backgroundBlurService: BackgroundBlurService,
    private _deviceService: DeviceService,
    permissions: MediaPermissionsService
  ) {
    // Stop cam stream when camera permission is revoked
    let prevCamAccess = permissions.cameraAccess();
    effect(() => {
      const camAccess = permissions.cameraAccess();
      if (prevCamAccess === 'granted' && camAccess !== 'granted' && this._camStream.value) {
        this.stopCamStream('camera permission revoked');
      }
      prevCamAccess = camAccess;
    });

    // Keep the blur service's amount in sync independent of stream state.
    _mediaConfigStore.backgroundBlurAmount$.subscribe((amount) => {
      this._backgroundBlurService.setBlurAmount(amount);
    });

    // Restart the camera when a config knob affecting the pipeline changes.
    // A restart is a single queued operation, so it can never interleave
    // with an in-flight start or stop.
    merge(
      _mediaConfigStore.videoDevice$.pipe(
        distinctUntilChanged((prev, curr) => prev?.id === curr?.id),
        map(() => 'device change')
      ),
      _mediaConfigStore.backgroundBlurEnabled$.pipe(map(() => 'background blur change')),
      _mediaConfigStore.backgroundBlurAmount$.pipe(map(() => 'background blur amount change'))
    ).subscribe((reason) => {
      this._restartCamStream(reason).catch((e) => {
        console.error(`cam restart failed (${reason})`, e);
      });
    });
  }

  private _enqueue<T>(fn: () => Promise<T>): Promise<T> {
    const next = this._queue.then(fn, fn);
    // Swallow rejections on the chain so one failure doesn't poison
    // subsequent operations; callers still see the rejection via `next`.
    this._queue = next.catch(() => {});
    return next;
  }

  get camStream(): MediaStream | null {
    return this._camStream.value;
  }

  get screenCastStream(): MediaStream | null {
    return this._screenCastStream.getValue();
  }

  /**
   * Starts the camera stream and applies optional background blur.
   *
   * The persisted device selection is resolved against the live device list
   * first (see DeviceService.resolveVideoDeviceIdForCapture) — a stored id
   * never reaches a deviceId constraint unverified. If the camera cannot
   * meet the strict resolution constraints, it retries with ideal-only, and
   * as a last resort without a device constraint at all.
   *
   * Returns the existing stream when already started. Failures set
   * `camError` and reject.
   */
  startCamStream(): Promise<MediaStream> {
    return this._enqueue(() => this._startCamStreamImpl());
  }

  stopCamStream(source: string): void {
    void this._enqueue(async () => this._stopCamStreamImpl(source));
  }

  private _restartCamStream(reason: string): Promise<void> {
    return this._enqueue(async () => {
      if (!this._camStream.value) return;
      this._stopCamStreamImpl(reason);
      await this._startCamStreamImpl();
    });
  }

  private async _startCamStreamImpl(): Promise<MediaStream> {
    if (this.camStream) return this.camStream;
    if (isTauri) return this._startNativeCamStream();
    try {
      const deviceId = await this._deviceService.resolveVideoDeviceIdForCapture();
      const settings =
        this._mediaConfigStore.value.cameraSettingsOverride ?? DEFAULT_CAMERA_SETTINGS;
      const constraints = buildCameraConstraints(settings, deviceId, true);

      let rawMs: MediaStream;
      try {
        rawMs = await navigator.mediaDevices.getUserMedia({ video: constraints });
      } catch {
        try {
          // Camera can't meet min resolution, retry with ideal only (no min)
          const fallback = buildCameraConstraints(settings, deviceId);
          rawMs = await navigator.mediaDevices.getUserMedia({ video: fallback });
        } catch (e) {
          if (!deviceId) throw e;
          // Last resort: the resolved device vanished between enumeration
          // and open — retry on the default camera instead of failing.
          rawMs = await navigator.mediaDevices.getUserMedia({
            video: buildCameraConstraints(settings),
          });
        }
      }

      // If Source resolution is selected, apply the device's largest 16:9 frame
      if (settings.resolution.width === 0 && settings.resolution.height === 0) {
        const track = rawMs.getVideoTracks()[0];
        const capabilities = track?.getCapabilities?.();
        const sourceConstraints =
          capabilities && buildSourceCameraConstraints(settings, capabilities);
        if (sourceConstraints) {
          await track.applyConstraints(sourceConstraints);
        }
      }

      // Store the raw stream for cleanup
      this._rawCamStream = rawMs;

      rawMs?.getTracks().forEach((track) => {
        track.onended = () => {
          this.stopCamStream('track ended');
        };
      });

      // Apply background blur if enabled
      let ms = rawMs;
      if (this._mediaConfigStore.value.backgroundBlurEnabled) {
        try {
          ms = await this._backgroundBlurService.applyBlur(rawMs);
        } catch (error) {
          console.error('startCamStream: Failed to apply background blur, using raw stream', error);
          // Fall back to raw stream if blur fails
          ms = rawMs;
        }
      }

      this._camError.set(null);
      this._camStream.next(ms);
      return ms;
    } catch (e) {
      this._camError.set(mapCamError(e));
      throw e;
    }
  }

  /**
   * Native camera: capture and encoding run in the plugin, which hands out a
   * placeholder MediaStream for the VideoInput and the canvas previews.
   * Background blur is a browser (MediaPipe) feature and not applied here;
   * the settings say so instead of offering it.
   */
  private async _startNativeCamStream(): Promise<MediaStream> {
    try {
      const { openCamera } = await import('@4players/odin-plugin-tauri/capture');
      let deviceId = await this._deviceService.resolveVideoDeviceIdForCapture();
      deviceId ??= (await this._deviceService.getVideoDevices())[0]?.deviceId;
      if (!deviceId) throw Object.assign(new Error('No camera found'), { name: 'NotFoundError' });
      const settings =
        this._mediaConfigStore.value.cameraSettingsOverride ?? DEFAULT_CAMERA_SETTINGS;
      const capture = await openCamera({ deviceId, config: nativeCaptureConfig(settings) });
      this._nativeCam = capture;
      this._camError.set(null);
      this._camStream.next(capture.stream);
      return capture.stream;
    } catch (e) {
      this._camError.set(mapCamError(e));
      throw e;
    }
  }

  private _stopCamStreamImpl(source: string): void {
    if (this._nativeCam) {
      void this._nativeCam.close();
      this._nativeCam = null;
      this._camError.set(null);
      this._camStream.next(null);
      return;
    }
    // Stop background blur processing if active
    if (this._backgroundBlurService.isProcessing()) {
      this._backgroundBlurService.stopProcessing();
    }

    // Stop the processed stream tracks
    this._camStream.value?.getTracks().forEach((track) => {
      track.stop();
      this._camStream.value?.removeTrack(track);
    });

    // Stop the raw camera stream tracks (these are different when blur is enabled)
    if (this._rawCamStream) {
      this._rawCamStream.getTracks().forEach((track) => {
        track.stop();
        this._rawCamStream?.removeTrack(track);
      });
      this._rawCamStream = null;
    }

    this._camError.set(null);
    this._camStream.next(null);
  }

  async startScreenCastStream(): Promise<MediaStream | null> {
    const settings = this._mediaConfigStore.value.screenSettingsOverride ?? DEFAULT_SCREEN_SETTINGS;
    if (!screenShareSupported) return null;
    if (isTauri) return this._startNativeScreenCastStream(settings);
    const constraints = buildScreenConstraints(settings);

    try {
      const ms = await navigator.mediaDevices.getDisplayMedia({
        video: constraints,
      });

      ms?.getTracks().forEach((track) => {
        track.onended = () => {
          this.stopScreenCastStream();
        };
      });
      this._screenCastStream.next(ms);
      return ms;
    } catch (e) {
      this._screenCastStream.next(null);
      return null;
    }
  }

  /** Desktop app only: the plugin shows the OS picker and captures natively. */
  private async _startNativeScreenCastStream(settings: VideoSettings): Promise<MediaStream | null> {
    try {
      const { openScreen } = await import('@4players/odin-plugin-tauri/capture');
      const capture = await openScreen({
        config: nativeCaptureConfig(settings),
        onEnded: () => this.stopScreenCastStream(),
      });
      this._nativeScreen = capture;
      this._screenCastStream.next(capture.stream);
      return capture.stream;
    } catch (e) {
      console.warn('screen share did not start', e);
      this._screenCastStream.next(null);
      return null;
    }
  }

  stopScreenCastStream() {
    if (this._nativeScreen) {
      void this._nativeScreen.close();
      this._nativeScreen = null;
      this._screenCastStream.next(null);
      return;
    }
    this.screenCastStream?.getVideoTracks().forEach((track) => {
      this.screenCastStream?.removeTrack(track);
      track.stop();
    });
    this._screenCastStream.next(null);
  }

  /**
   * Enable or disable background blur for the camera stream.
   * If the camera is active, this will restart the stream with blur applied/removed.
   */
  setBackgroundBlurEnabled(enabled: boolean): void {
    this._mediaConfigStore.update({ backgroundBlurEnabled: enabled });
  }

  /**
   * Get whether background blur is currently enabled
   */
  get backgroundBlurEnabled(): boolean {
    return this._mediaConfigStore.value.backgroundBlurEnabled;
  }

  /**
   * Set the blur intensity (0-50 pixels)
   */
  setBackgroundBlurAmount(amount: number): void {
    const clampedAmount = Math.max(0, Math.min(50, amount));
    this._mediaConfigStore.update({ backgroundBlurAmount: clampedAmount });
  }

  /**
   * Get the current blur intensity
   */
  get backgroundBlurAmount(): number {
    return this._mediaConfigStore.value.backgroundBlurAmount;
  }

  /**
   * Pre-initialize the background blur service (loads the ML model).
   * Call this early to reduce delay when blur is first enabled.
   */
  async preloadBackgroundBlur(): Promise<void> {
    await this._backgroundBlurService.initialize();
  }

  /**
   * Get the background blur service state
   */
  get backgroundBlurState() {
    return this._backgroundBlurService.state;
  }
}
